import moment from "moment";
import HasLabel from "./HasLabel";
import { Order, OrderStatus } from "./Order";
import TrackedObject from "./TrackedObject";
import TextUtils from "@/util/TextUtils";
import { Patient } from "@/models/Patient";
import { Program } from "@/models/Program";
import { Claim } from "./Claim";
import { Prescriber } from "@/models/Prescriber";
import { DeliveryCode } from '@/models/DeliveryCode'
import { DrugSource } from "@/models/Drug/DrugProduct";
import { ReminderSupression } from "./ReminderSuppression";

export interface Prescription extends TrackedObject {
    storeID: number;
    rxNumber: number;
    rfNumber?: number;
    imageID?: number;
    escriptID?: number;
    programTransferId?: number;
    directions: string;
    daySupply: number;
    quantity: number;
    dispensed: number;
    refills: number;
    originCode: OriginCode;
    dawCode: DAWCode;
    status: RefillStatusCode;
    subStatus: number | null;
    subStatusName?: string | null;
    memo: string;
    programName: string;
    storeName: string;
    lotNumber: string;
    drugExpirationDate: Date | null;
    totalPrice: number | null;
    cost: number | null;
    patientPay: number | null;
    tax: number | null;
    deliveryCode: DeliveryCode;
    deliveryOption: string;
    packageID: number;
    drugSource: DrugSource;
    patientID: number;
    prescriberID: number;
    prescriberName?: string;
    programID?: number;
    orderId: number;
    originalRxDate: Date;
    fillDate: Date;
    writtenDate: Date;
    expirationDate: Date | null;
    drugsFilledDate: Date | null;
    annotations: string;
    drugName: string;
    ndc11?: string;
    productNameShort: string;
    pV1ID: number;
    pV2ID: number | null;
    pv1: Date;
    pv1By: string;
    pv2: Date;
    pv2By: string;
    dispenseErrorId?: number;
    dispenseErrorStatus?: string;
    canBeFilled: boolean;
    totalDispensed: number;
    totalAllowedToDispense: number;
    priceScheduleID?: number;
    deaClassificationID?: number;
    isPV1Required?: boolean;
    requiresPV1: boolean;
    isAvailableForAutorefill: boolean;
    isAutoRefillEnrolled?: boolean;

    orders: Array<Order>;
    patient: Patient;
    prescriber: Prescriber;
    claims: Array<Claim>;
    program: Program;
    suppression: ReminderSupression;

    serialNumber: string;
    partnerFillNumber: string;
    originPharmacyId?: number;
    transferMyRxId?: string;
    isUrgent: boolean;
    treatmentTypeCode?: TreatmentType | null;
    nextTouchDate: Date | null;
    nextTouchDateManualOverride: boolean;
    compoundPresetID?: number | null;

    dispensingItems?: PrescriptionDispenseItem[];

    estimatedCost: number;
    isExistingFill?: boolean;
}

export interface PrescriptionDispenseItem {
    lotNumber?: string;
    serialNumber?: string;
    expirationDate?: Date | null;
    quantity?: number | null;
    gtin?: string;
    source?: DispenseItemSource | null;
}

export enum DispenseItemSource {
    Manual = 0,
    DataMatrix = 1,
    UPC = 2,
    Parata = 3
}

export interface FaxForm {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    fax: string;
    dea: string;
    pharmacist: string;
    verbalTransfer: boolean | null;
    note: string;
    alternateDrug: string;
    rxID: string;
    patientID: number;
    prescriberID: number;
    packageID: number;
    drugSource: number;
    storedPharmacyId: number;
    patientPlanIds: number[];
    storeID: number;
    appendPrescriptionImage: boolean;
    imageIds?: number[] | null;
    excludeRefillRequestsFromNotes: boolean;
    programID?: number | null;
}

export class FaxForm {
    constructor() {
        this.name = '';
        this.address = '';
        this.city = '';
        this.state = '';
        this.zip = '';
        this.phone = '';
        this.fax = '';
        this.dea = '';
        this.pharmacist = '';
        this.verbalTransfer = null;
        this.note = '';
        this.alternateDrug = '';
        this.storedPharmacyId = 0;
        this.patientPlanIds = [];
        this.storeID = 0;
        this.appendPrescriptionImage = false;
        this.imageIds = [];
        this.excludeRefillRequestsFromNotes = false;
    }
}

export enum OriginCode {
    NotSpecified,
    Written,
    Phone,
    Escript,
    Fax,
    Transfer,
    Custom
}

export enum TreatmentType {
    NotForOpioidDependency = 1,
    OpioidDependencyTreatment = 2,
    CancerTreatmentPain = 3,
    PalliativeCare = 4,
    EndOfLifeHospice = 5,
    PregnantWithOpioidRx = 6,
    AcutePainWithChronicOpioid = 7,
    ActiveOpioidTaper = 8,
    PainManagementContract = 9,
    AcuteOpioidTherapy = 10,
    ChronicOpioidTherapy = 11,
    Other = 99,
}

export enum DAWCode {
    NotSpecified,
    Prescriber,
    Patient,
    Pharmacy,
    GenericNotStocked,
    DispenseAsGeneric,
    Override,
    SubstitutionNotAllowed,
    SubstitutionOkNoGeneric,
    Other
}

export enum RefillStatusCode {
    Refillable = 0,
    AutoRefillable = 1,
    NonRefillable = 2,
    Suspended = 3,
    TransferOut = 4,
    TransferIn = 5,
    OnHold = 6,
    Canceled = 7,
    Expired = 8,
    Void = 9,
    AwaitingInsurance = 10,
    Discontinued = 11,
}

export const RxDeniedSubStatusId = 14;

export class Prescription extends HasLabel {
    constructor(obj?: Prescription) {
        super();
        this.requiresPV1 = false;
        this.fillDate = moment().utc(true).toDate();
        this.status = RefillStatusCode.Refillable;
        this.deliveryCode = DeliveryCode.Standard;
        this.patientID = 0;
        this.storeID = 0;
        this.packageID = 0;
        this.prescriberID = 0;
        this.isAvailableForAutorefill = false;
        this.isAutoRefillEnrolled = false;
        this.programID = 0;
        this.program = new Program();

        if (obj) Object.assign(this, obj);
    }

    toString(): string {
        if (!this.rxNumber) return '<div class="font-weight-light">Store-Rx-Rf</div>';

        const parts = [];

        if (this.rxNumber) {
            parts.push(`<strong>${this.rxID}</strong>`);
        }

        if (this.productNameShort) {
            parts.push(this.productNameShort);
        }

        if (this.prescriberName) {
            parts.push(`Prescriber: ${this.prescriberName}`);
        }

        if (this.writtenDate) {
            parts.push(`Written: ${moment(this.writtenDate).format("YYYY-MM-DD")}`);
        }

        if (this.patientID) {
            parts.push(`Patient Id: ${this.patientID}`);
        }

        return parts.join(', ');
    }

    // This has a computed getter but might be populated from the backend, an empty setter allows the object to be assigned without error
    set rxID(_:string){}

    get rxID(): string {
        return Prescription.createRxId(this.storeID, this.rxNumber, this.rfNumber);
    }

    get isActive(): boolean {
        return !(this.status == RefillStatusCode.TransferOut || this.isVoid) && !this.isExpired;
    }

    /**
     * Indicates if prescription is Void or RxDenied.
     */
    get isVoid(): boolean {
        return this.status == RefillStatusCode.Void
            || this.status == RefillStatusCode.Discontinued
            || (this.status == RefillStatusCode.OnHold && this.subStatus == RxDeniedSubStatusId);
    }

    /**
     * Indicates if the program allows the prescription to refill even if previous refill has not been shipped.
     */
    get programAllowsRefillsIfUnshipped(): boolean {
        if (!this.program?.flags.length) return false;

        return this.program.flags.some(f => f.value && f.name == "Allow Refills if Unshipped");
    }

    get isRefillable(): boolean {
        //const hasBeenPV2 = !!this.pV2ID;
        const rx30ScriptUpdatedByCli = (this.createdBy == 'Rx30' && this.updatedBy == 'CPHub/CLI');

        return this.canBeFilled && this.isRefillableStatus
            && (this.hasBeenShipped || this.programAllowsRefillsIfUnshipped || this.updatedBy == 'Rx30' || rx30ScriptUpdatedByCli);
        // && ((this.hasBeenShipped && hasBeenPV2) || this.fillDate < moment('04/01/2021').toDate());
    }

    get isRefillableStatus(): boolean {
        return (this.status == RefillStatusCode.Refillable || this.status == RefillStatusCode.AutoRefillable
            || this.status == RefillStatusCode.TransferIn) && this.isActive;
    }

    get isExpired(): boolean {
        if (!this.expirationDate) return false;

        const expMoment = moment(this.expirationDate);
        return moment().isAfter(expMoment, 'days');
    }

    /**
     * Indicates if the prescription is in a order that has already been shipped.
     */
    get hasBeenShipped(): boolean {
        return this.orders?.some(o => o.status == OrderStatus.Shipped) ?? false;
    }

    get hasBeenVerified(): boolean {
        return (this.pV2ID ?? 0) > 0;
    }

    get hasFullId(): boolean {
        return !!(this.storeID && this.rxNumber && (this.rfNumber || this.rfNumber == 0));
    }

    get statusName(): string {
        if (this.isExpired) return "Expired";
        return TextUtils.camelCaseToNormal(RefillStatusCode[this.status]);
    }

    get isCashedOut(): boolean {
        return !!(this.priceScheduleID);
    }

    get isRx30BeforeApril(): boolean {
        const myMoment = moment;
        return (this.status != RefillStatusCode.OnHold &&
            myMoment(this.fillDate).isSameOrBefore(myMoment("2021-04-01")) &&
            this.createdBy == 'Rx30')
            || (this.status != RefillStatusCode.OnHold &&
                (this.updatedBy == 'Rx30' || this.updatedBy == 'CPHub/CLI') &&
                this.memo == "Transferred from Rx30 post-launch");
    }

    public static createRxId(storeID: number, rxNumber: number, rfNumber: number | undefined): string {
        return `${storeID || ''}-${rxNumber || ''}-${rfNumber == undefined ? '' : (rfNumber || 0)}`;
    }

    get remainingQuantity(): number {
        if (!this.rxNumber || this.rfNumber == 0) return (this.quantity * ((this.refills || 0) + 1));

        const res = this.totalAllowedToDispense - (this.totalDispensed || 0);
        return Number(res.toFixed(2));
    }

    get isCII(): boolean {
        return this.deaClassificationID == 1 || this.deaClassificationID == 2;
    }

}
